import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

interface Props {
  onLeagueCreated: () => void;
}

function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function getTodayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function getDefaultEndDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return d.toISOString().split('T')[0];
}

interface SuccessLeague {
  id: string;
  name: string;
  sport: string;
  bankroll: number;
  max_members: number;
  type: string;
  start_date: string;
  access_code: string | null;
}

export default function CreateLeagueForm({ onLeagueCreated }: Props) {
  const { isRegistered, supabaseUser, openLoginModal } = useAuth();

  const [name, setName] = useState('');
  const [sport, setSport] = useState('Fútbol');
  const [bankroll, setBankroll] = useState('1000');
  const [maxMembers, setMaxMembers] = useState('50');
  const [type, setType] = useState<'Pública' | 'Privada'>('Pública');
  const [startDate, setStartDate] = useState(getTodayISO());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [successLeague, setSuccessLeague] = useState<SuccessLeague | null>(null);
  const [serverError, setServerError] = useState('');

  // Not logged in — prompt login
  if (!isRegistered) {
    return (
      <div className="max-w-xl">
        <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-10 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#ffd700]/10 mx-auto mb-4">
            <i className="ri-lock-line text-[#ffd700] text-3xl"></i>
          </div>
          <h3 className="text-white font-black text-lg mb-2">Inicia sesión para crear una liga</h3>
          <p className="text-[#a8b2c1] text-sm mb-6">
            Necesitas una cuenta para crear y administrar ligas competitivas.
          </p>
          <button
            onClick={openLoginModal}
            className="px-6 py-3 bg-[#ffd700] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#f0c800] transition-all whitespace-nowrap"
          >
            Iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim() || name.trim().length < 3) newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    if (!description.trim() || description.trim().length < 10)
      newErrors.description = 'Añade una descripción (mín. 10 caracteres)';
    if (!startDate) newErrors.startDate = 'Elige la fecha de inicio';
    if (!endDate) newErrors.endDate = 'Elige la fecha de fin';
    if (startDate && endDate && endDate <= startDate)
      newErrors.endDate = 'La fecha de fin debe ser posterior al inicio';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!supabaseUser) return;

    setErrors({});
    setServerError('');
    setSubmitting(true);

    const accessCode = type === 'Privada' ? generateCode() : null;

    const { data, error } = await supabase
      .from('leagues')
      .insert({
        name: name.trim(),
        sport,
        type,
        bankroll: parseInt(bankroll),
        max_members: parseInt(maxMembers),
        start_date: startDate,
        end_date: endDate,
        description: description.trim(),
        access_code: accessCode,
        admin_id: supabaseUser.id,
        tags: [sport],
        status: 'active',
      })
      .select('id, name, sport, bankroll, max_members, type, start_date, access_code')
      .maybeSingle();

    setSubmitting(false);

    if (error || !data) {
      setServerError('Error al crear la liga. Inténtalo de nuevo.');
      return;
    }

    // Auto-join as admin
    await supabase.from('league_members').insert({
      league_id: data.id,
      user_id: supabaseUser.id,
      current_bankroll: parseInt(bankroll),
    });

    setSuccessLeague(data as SuccessLeague);
  };

  const handleReset = () => {
    setName(''); setSport('Fútbol'); setBankroll('1000'); setMaxMembers('50');
    setType('Pública'); setStartDate(getTodayISO()); setEndDate(getDefaultEndDate());
    setDescription(''); setErrors({}); setSuccessLeague(null); setServerError('');
  };

  const handleGoExplore = () => {
    onLeagueCreated();
  };

  // ── Success screen ──────────────────────────
  if (successLeague) {
    return (
      <div className="max-w-xl">
        <div className="bg-[#151719] border border-[#00e87a]/30 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00e87a]/15 mx-auto mb-4">
            <i className="ri-checkbox-circle-line text-[#00e87a] text-3xl"></i>
          </div>
          <h3 className="text-white font-black text-xl mb-2">¡Liga creada!</h3>
          <p className="text-[#a8b2c1] text-sm mb-6">
            <strong className="text-white">{successLeague.name}</strong> ya está activa y visible para todos.
          </p>

          <div className="grid grid-cols-2 gap-3 mb-6">
            {[
              { label: 'Deporte', value: successLeague.sport, icon: 'ri-football-line' },
              { label: 'Bankroll', value: `${successLeague.bankroll.toLocaleString('es-ES')} u`, icon: 'ri-coin-line' },
              { label: 'Plazas', value: successLeague.max_members.toString(), icon: 'ri-group-line' },
              {
                label: 'Inicio',
                value: new Date(successLeague.start_date).toLocaleDateString('es-ES'),
                icon: 'ri-calendar-line',
              },
            ].map((s) => (
              <div key={s.label} className="bg-[#0d0f14] rounded-xl p-3 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center text-[#00e87a]">
                  <i className={`${s.icon} text-base`}></i>
                </div>
                <div className="text-left">
                  <p className="text-[#6b7280] text-xs">{s.label}</p>
                  <p className="text-white text-sm font-bold">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          {successLeague.type === 'Privada' && successLeague.access_code && (
            <div className="bg-[#ffd700]/5 border border-[#ffd700]/30 rounded-xl p-4 mb-6">
              <p className="text-[#ffd700] text-xs font-bold uppercase tracking-widest mb-2">
                Código de acceso privado
              </p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-[#ffd700] font-black text-2xl tracking-[0.3em]">
                  {successLeague.access_code}
                </span>
                <button
                  onClick={() => navigator.clipboard.writeText(successLeague.access_code!)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ffd700]/15 text-[#ffd700] hover:bg-[#ffd700]/25 transition-colors cursor-pointer"
                >
                  <i className="ri-file-copy-line text-sm"></i>
                </button>
              </div>
              <p className="text-[#6b7280] text-xs mt-2">
                Comparte este código con quienes quieras invitar
              </p>
            </div>
          )}

          {successLeague.type === 'Pública' && (
            <div className="bg-[#00e87a]/5 border border-[#00e87a]/20 rounded-xl p-4 mb-6">
              <p className="text-[#00e87a] text-sm font-semibold">Liga pública activa</p>
              <p className="text-[#6b7280] text-xs mt-1">
                Aparece en &ldquo;Explorar Ligas&rdquo; — cualquier usuario puede unirse
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap hover:text-white transition-colors"
            >
              Crear otra liga
            </button>
            <button
              onClick={handleGoExplore}
              className="flex-1 py-3 bg-[#ffd700] text-black font-black text-sm rounded-xl cursor-pointer hover:bg-[#f0c800] transition-colors whitespace-nowrap"
            >
              Ver mis ligas
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────
  return (
    <div className="max-w-xl">
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#ffd700]/15 text-[#ffd700]">
            <i className="ri-trophy-line text-lg"></i>
          </div>
          <div>
            <h3 className="text-white font-black text-lg">Configurar Nueva Liga</h3>
            <p className="text-[#6b7280] text-xs">Todos los campos son obligatorios</p>
          </div>
        </div>

        {serverError && (
          <div className="mb-4 px-4 py-3 bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-xl text-[#ef4444] text-sm">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
              Nombre de la Liga
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: '' })); }}
              placeholder="Ej: Liga Corners Masters Primavera 2026..."
              className={`w-full bg-[#0d0f14] border text-white text-sm rounded-xl px-4 py-3 placeholder-[#6b7280] focus:outline-none transition-colors ${
                errors.name ? 'border-[#ef4444]' : 'border-[#2a2d3a] focus:border-[#ffd700]/40'
              }`}
            />
            {errors.name && <p className="text-[#ef4444] text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Sport + Bankroll */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
                Deporte
              </label>
              <select
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-4 py-3 cursor-pointer focus:outline-none focus:border-[#ffd700]/40"
              >
                {['Fútbol', 'Tenis', 'Baloncesto', 'Multi-sport'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
                Bankroll inicial
              </label>
              <select
                value={bankroll}
                onChange={(e) => setBankroll(e.target.value)}
                className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-4 py-3 cursor-pointer focus:outline-none focus:border-[#ffd700]/40"
              >
                {[['500','500 unidades'],['1000','1.000 unidades'],['2000','2.000 unidades'],['5000','5.000 unidades']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Max members + Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
                Máx. participantes
              </label>
              <select
                value={maxMembers}
                onChange={(e) => setMaxMembers(e.target.value)}
                className="w-full bg-[#0d0f14] border border-[#2a2d3a] text-white text-sm rounded-xl px-4 py-3 cursor-pointer focus:outline-none focus:border-[#ffd700]/40"
              >
                {[['10','10'],['15','15'],['20','20'],['30','30'],['50','50'],['100','100'],['200','200']].map(([v,l]) => (
                  <option key={v} value={v}>{l} participantes</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
                Tipo de Liga
              </label>
              <div className="flex gap-2 h-[46px]">
                {(['Pública', 'Privada'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold rounded-xl border transition-all cursor-pointer whitespace-nowrap ${
                      type === t
                        ? t === 'Pública'
                          ? 'bg-[#00e87a]/15 border-[#00e87a]/40 text-[#00e87a]'
                          : 'bg-[#ff6b35]/15 border-[#ff6b35]/40 text-[#ff6b35]'
                        : 'border-[#2a2d3a] text-[#6b7280] hover:text-white'
                    }`}
                  >
                    <i className={`${t === 'Privada' ? 'ri-lock-line' : 'ri-global-line'} text-sm`}></i>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {type === 'Privada' && (
            <div className="bg-[#ff6b35]/5 border border-[#ff6b35]/20 rounded-xl px-4 py-3 flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center text-[#ff6b35] mt-0.5 flex-shrink-0">
                <i className="ri-information-line text-sm"></i>
              </div>
              <p className="text-[#a8b2c1] text-xs leading-relaxed">
                Se generará un{' '}
                <strong className="text-[#ff6b35]">código único de 6 caracteres</strong> que deberás
                compartir con los participantes que quieras invitar.
              </p>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
                Fecha de inicio
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setErrors((p) => ({ ...p, startDate: '' })); }}
                className={`w-full bg-[#0d0f14] border text-white text-sm rounded-xl px-4 py-3 focus:outline-none transition-colors ${
                  errors.startDate ? 'border-[#ef4444]' : 'border-[#2a2d3a] focus:border-[#ffd700]/40'
                }`}
              />
              {errors.startDate && <p className="text-[#ef4444] text-xs mt-1">{errors.startDate}</p>}
            </div>
            <div>
              <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
                Fecha de fin
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => { setEndDate(e.target.value); setErrors((p) => ({ ...p, endDate: '' })); }}
                className={`w-full bg-[#0d0f14] border text-white text-sm rounded-xl px-4 py-3 focus:outline-none transition-colors ${
                  errors.endDate ? 'border-[#ef4444]' : 'border-[#2a2d3a] focus:border-[#ffd700]/40'
                }`}
              />
              {errors.endDate && <p className="text-[#ef4444] text-xs mt-1">{errors.endDate}</p>}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[#a8b2c1] text-xs font-semibold uppercase tracking-wide mb-1.5">
              Descripción de la liga
            </label>
            <textarea
              value={description}
              onChange={(e) => { setDescription(e.target.value); setErrors((p) => ({ ...p, description: '' })); }}
              placeholder="Describe el objetivo de la liga, las normas, qué mercados se permiten..."
              rows={3}
              maxLength={300}
              className={`w-full bg-[#0d0f14] border text-white text-sm rounded-xl px-4 py-3 placeholder-[#6b7280] focus:outline-none transition-colors resize-none ${
                errors.description ? 'border-[#ef4444]' : 'border-[#2a2d3a] focus:border-[#ffd700]/40'
              }`}
            />
            <div className="flex justify-between mt-1">
              {errors.description ? (
                <p className="text-[#ef4444] text-xs">{errors.description}</p>
              ) : <span />}
              <span className="text-[#6b7280] text-xs ml-auto">{description.length}/300</span>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-[#0d0f14] rounded-xl p-4 border border-[#2a2d3a]">
            <p className="text-[#6b7280] text-xs uppercase tracking-wide mb-3">Resumen</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Bankroll', value: `${parseInt(bankroll).toLocaleString('es-ES')} u` },
                { label: 'Participantes', value: `Máx. ${maxMembers}` },
                {
                  label: 'Duración',
                  value:
                    startDate && endDate
                      ? `${Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000 / 7))} semanas`
                      : '—',
                },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-white font-bold text-sm">{s.value}</p>
                  <p className="text-[#6b7280] text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-[#ffd700] text-black font-black text-sm rounded-xl hover:bg-[#f0c800] transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black/40 border-t-black rounded-full animate-spin"></div>
                Creando liga...
              </>
            ) : (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-trophy-line text-sm"></i>
                </div>
                Crear Liga {type === 'Privada' ? '+ Generar Código' : 'Ahora'}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
